import type { DeploymentOutcome, DeploymentServingState, ProjectDeployment } from '../../api';
import { rowState } from '../status/statusModel';

/**
 * Deployment history filters (CO-150). They live in the URL (`q`,
 * `component`, `outcome`, `since`), so opening a deployment and going back
 * keeps them, and a filtered view can be shared. They apply to the whole
 * history the API returned — every deployment of the environment — not to
 * a page of it.
 */

export type OutcomeFilter = 'all' | 'failed' | 'succeeded' | 'partial' | 'in_progress' | 'rolled_back' | 'cancelled';
export type SinceFilter = 'all' | '24h' | '7d' | '30d';

export interface DeploymentFilters {
  q: string;
  component: string;
  outcome: OutcomeFilter;
  since: SinceFilter;
}

export const outcomeFilterLabel: Record<OutcomeFilter, string> = {
  all: 'Any outcome',
  failed: 'Failed',
  succeeded: 'Succeeded',
  partial: 'Partially succeeded',
  in_progress: 'In progress or queued',
  rolled_back: 'Rolled back',
  cancelled: 'Cancelled or rehearsal',
};

export const sinceFilterLabel: Record<SinceFilter, string> = {
  all: 'Any time',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

const outcomeMatches: Record<Exclude<OutcomeFilter, 'all'>, DeploymentOutcome[]> = {
  failed: ['failed'],
  succeeded: ['succeeded'],
  partial: ['succeeded_with_failures'],
  in_progress: ['queued', 'deploying'],
  rolled_back: ['rolled_back'],
  cancelled: ['cancelled', 'rehearsal'],
};

const sinceMs: Record<Exclude<SinceFilter, 'all'>, number> = { '24h': 86_400_000, '7d': 7 * 86_400_000, '30d': 30 * 86_400_000 };

export function filtersFrom(params: URLSearchParams): DeploymentFilters {
  const outcome = params.get('outcome');
  const since = params.get('since');
  return {
    q: params.get('q') ?? '',
    component: params.get('component') ?? '',
    outcome: outcome && outcome in outcomeFilterLabel ? (outcome as OutcomeFilter) : 'all',
    since: since && since in sinceFilterLabel ? (since as SinceFilter) : 'all',
  };
}

/** Writes the filters into a copy of the params, dropping the defaults. */
export function withFilters(params: URLSearchParams, filters: DeploymentFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  const set = (key: string, value: string, fallback: string) => (value && value !== fallback ? next.set(key, value) : next.delete(key));
  set('q', filters.q.trim(), '');
  set('component', filters.component, '');
  set('outcome', filters.outcome, 'all');
  set('since', filters.since, 'all');
  return next;
}

export function hasFilters(filters: DeploymentFilters): boolean {
  return Boolean(filters.q.trim() || filters.component || filters.outcome !== 'all' || filters.since !== 'all');
}

/** The component a deployment belongs to, as the filter and the table name it. */
export function componentName(deployment: ProjectDeployment): string {
  return deployment.component_name || (deployment.scope === 'combined' ? 'Magento and frontend' : 'Magento');
}

/** Every component in the history, in name order. */
export function componentOptions(deployments: ProjectDeployment[]): string[] {
  return [...new Set(deployments.map(componentName))].sort((a, b) => a.localeCompare(b));
}

/** The text a search matches: commit, message, branch, component, release. */
function searchable(deployment: ProjectDeployment): string {
  return [
    deployment.source_commit_sha,
    deployment.frontend_commit_sha,
    deployment.source_commit_message,
    deployment.frontend_commit_message,
    deployment.source_branch,
    deployment.frontend_commit_ref,
    componentName(deployment),
    deployment.created_by,
    ...(deployment.components ?? []).map((component) => component.release_tag),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function applyFilters(
  deployments: ProjectDeployment[],
  filters: DeploymentFilters,
  serving: Record<string, DeploymentServingState> | null | undefined,
  now: number = Date.now(),
): ProjectDeployment[] {
  const needle = filters.q.trim().toLowerCase();
  return deployments.filter((deployment) => {
    if (filters.component && componentName(deployment) !== filters.component) return false;
    if (filters.outcome !== 'all' && !outcomeMatches[filters.outcome].includes(rowState(deployment, serving).outcome)) return false;
    if (filters.since !== 'all' && now - Date.parse(deployment.created_at) > sinceMs[filters.since]) return false;
    if (needle && !needle.split(/\s+/).every((word) => searchable(deployment).includes(word))) return false;
    return true;
  });
}
